import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Barber, BarberStatus } from '@/types';

/**
 * Hook to get barbers available TODAY
 * Uses real-time subscription to get immediate updates when barbers change status
 * Filters by:
 * - isActive === true (in Firestore query)
 * - schedule[today].isWorking === true (locally)
 * - barberStatus: not day_off, not offline (real-time from barberStatus collection)
 * - branchId (if provided, in Firestore query)
 */
export function useAvailableBarbers(branchId?: string) {
  const [availableBarbers, setAvailableBarbers] = useState<Barber[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!branchId) {
      setAvailableBarbers([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Build query with filters
    const constraints = [where('isActive', '==', true)];
    if (branchId) {
      constraints.push(where('branchId', '==', branchId));
    }

    const q = query(collection(db, 'barbers'), ...constraints);

    // Track barber statuses separately for real-time updates
    const barberStatuses = new Map<string, BarberStatus>();
    const statusUnsubscribes: (() => void)[] = [];
    let currentBarbers: Barber[] = [];
    // Track how many barberStatus subscriptions have fired their initial snapshot
    let pendingStatusLoads = 0;
    let initialStatusLoadComplete = false;

    const filterAndSetBarbers = () => {
      // Don't emit results until ALL barberStatus subscriptions have fired at least once
      // This prevents a brief flash of unavailable barbers being shown as available
      if (!initialStatusLoadComplete && pendingStatusLoads > 0) {
        return;
      }

      const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const today = daysOfWeek[new Date().getDay()] as keyof Barber['schedule'];

      const available = currentBarbers.filter((barber) => {
        // Check isAvailable flag (set by admin, defaults to true if not set)
        if (barber.isAvailable === false) return false;

        // Check schedule: is the barber working today?
        const isWorkingToday = barber.schedule?.[today]?.isWorking === true;
        if (!isWorkingToday) return false;

        // Check real-time barberStatus: exclude day_off, offline and non-temporary breaks.
        //
        // Los descansos TEMPORALES (comiendo, gestión rápida, asuntos personales) NO
        // excluyen al barbero: el cliente puede coger turno y se le atiende al volver.
        // El tiempo de espera que ve ya incluye lo que resta de descanso (ver
        // useBarberWaitTime y calculateQueuePosition en functions).
        //
        // Antes se excluía cualquier `on_break`, lo que contradecía a
        // functions/src/queue/validators.ts —que sí permite reservar en descansos
        // temporales— y hacía que el barbero desapareciera al marcar el almuerzo.
        const status = barberStatuses.get(barber.barberId);
        if (status) {
          if (status.breakReason === 'day_off') return false;
          if (status.status === 'offline') return false;
          if (status.status === 'on_break' && !status.breakIsTemporary) return false;
        }

        return true;
      });

      setAvailableBarbers(available);
      setLoading(false);
    };

    // Subscribe to barbers collection
    const unsubscribeBarbers = onSnapshot(q, (snapshot) => {
      currentBarbers = snapshot.docs.map(doc => ({
        barberId: doc.id,
        ...doc.data()
      })) as Barber[];

      // Clean up old status subscriptions
      statusUnsubscribes.forEach(unsub => unsub());
      statusUnsubscribes.length = 0;
      barberStatuses.clear();
      initialStatusLoadComplete = false;

      if (currentBarbers.length === 0) {
        // No barbers at all - emit empty immediately
        initialStatusLoadComplete = true;
        filterAndSetBarbers();
        return;
      }

      // Track pending status loads
      pendingStatusLoads = currentBarbers.length;

      // Subscribe to each barber's status document
      currentBarbers.forEach((barber) => {
        const statusRef = doc(db, 'barberStatus', barber.barberId);
        const unsub = onSnapshot(statusRef, (statusDoc) => {
          if (statusDoc.exists()) {
            barberStatuses.set(barber.barberId, statusDoc.data() as BarberStatus);
          } else {
            barberStatuses.delete(barber.barberId);
          }

          // Decrement pending loads on first snapshot for each barber
          if (pendingStatusLoads > 0) {
            pendingStatusLoads--;
            if (pendingStatusLoads === 0) {
              initialStatusLoadComplete = true;
            }
          }

          filterAndSetBarbers();
        });
        statusUnsubscribes.push(unsub);
      });
    }, (error) => {
      console.error('Error subscribing to barbers:', error);
      setLoading(false);
    });

    return () => {
      unsubscribeBarbers();
      statusUnsubscribes.forEach(unsub => unsub());
    };
  }, [branchId]);

  return {
    availableBarbers,
    loading,
  };
}
